import { abilityIconSrcForKey } from "./abilityIcons.js";

/**
 * Aura icon + curation-category loader (#137/#139) — resolves an aura def id to
 * a bundled image URL and its nameplate CURATION category, entirely client-side
 * (presentation layer). SHARED by the self buff/debuff bar (#139) and the
 * nameplate/frame aura rows (#137/#138) — one icon pipeline, different feeds.
 *
 * BOUNDARY: icons + curation are cosmetic/presentation and MUST NOT touch the
 * sim or any hash.
 *
 * BUFF/DEBUFF POLARITY (#168) IS NO LONGER HERE. It is server-authored on the
 * aura def (`AuraDef.polarity`) and rides the wire (`WireAura.polarity`), so the
 * client reads it directly off the snapshot — there is NO local buff/debuff
 * mirror table and NO "unknown → buff" silent default any more. This file keeps
 * ONLY the CURATION `category` axis (#137, a nameplate-crowding filter), which is
 * a separate concern still resolved client-side by def id.
 *
 * ASSET RESOLUTION: PNGs live in `packages/content/assets/icons/auras/
 * <aura-id>.png` (see that folder's README + #72 spec: 256x256, transparent,
 * art-only). Vite's `import.meta.glob` maps each file to a content-hashed URL
 * at build time. `auraIconSrc(defId)` returns that URL, or `null` when no art
 * is committed — callers fall back to a letter glyph (`auraIconGlyph`), exactly
 * like the ActionBar ability-icon fallback (#72). Ships safely before any art.
 */

/**
 * Aura "category" axis (#137) — the CURATION dimension the nameplate /
 * unit-frame MAJOR-aura rows filter on. Distinct from buff/debuff polarity
 * (#168, now wire-carried on `WireAura.polarity`, which drives border colour):
 * category answers "is this worth a slot on a crowded nameplate?".
 *
 *   • "cc"        — hard/soft crowd control (stun, root, silence). Always major.
 *   • "defensive" — big cooldown mitigation / immunity (shield, spell-reflect).
 *   • "debuff"    — a key applied debuff worth tracking on an enemy.
 *   • "minor"     — everything else (incidental tick-DoTs, permanent passives,
 *                   uncategorised auras). Hidden from the major rows by default.
 *
 * The DEFAULT selection ({@link DEFAULT_NAMEPLATE_AURA_CATEGORIES}) shows
 * cc/defensive/debuff and hides minor; the exact set is user-customizable via
 * prefs (`hud.nameplateAuraCategories`, see prefs.ts) — never hardcoded at the
 * call site.
 */
export const AURA_CATEGORIES = ["cc", "defensive", "debuff", "minor"] as const;
export type AuraCategory = (typeof AURA_CATEGORIES)[number];

/**
 * Default MAJOR-aura selection for nameplates / unit frames (#137): the curated
 * "worth a slot" categories. `minor` is intentionally excluded so tick-DoTs and
 * permanent passives don't clutter a crowded nameplate. Prefs override this.
 */
export const DEFAULT_NAMEPLATE_AURA_CATEGORIES: readonly AuraCategory[] = [
  "cc",
  "defensive",
  "debuff",
];

/**
 * Display priority within a major-aura row: lower sorts first (leftmost). CC is
 * the most decision-relevant (peel / trinket), then big defensives, then
 * debuffs, then the (normally hidden) minor tail. A TOTAL order over the
 * category axis so row order never wobbles between equal auras.
 */
const CATEGORY_PRIORITY: Readonly<Record<AuraCategory, number>> = {
  cc: 0,
  defensive: 1,
  debuff: 2,
  minor: 3,
};

/** Sort weight for an aura category (lower = leftmost in a major-aura row). */
export function auraCategoryPriority(category: AuraCategory): number {
  return CATEGORY_PRIORITY[category];
}

/**
 * Client-side per-aura CURATION metadata, keyed by def id. Holds ONLY the #137
 * `category` (nameplate-crowding filter) — the buff/debuff polarity that used to
 * live here is now server-authored and wire-carried (`WireAura.polarity`, #168).
 * Add a row when a new aura def lands; an unknown def falls back to
 * {@link DEFAULT_AURA_META} (categorised `minor`, so it stays off nameplates by
 * default until a row is added).
 *
 * RESERVED (not built at v1): a `hidden`/`hideFromSelfBar` flag to exclude
 * no-op/internal carrier auras from the self bar. v1 shows literally EVERY own
 * aura (all current defs are player-meaningful — a DoT, CC, or defensive buff —
 * none are internal carriers). Add the flag + a `selfBarAuras` filter here when
 * a genuinely-internal aura lands, mirroring the reserved dispel-type axis.
 */
interface AuraMeta {
  /** Curation category (#137) — drives the nameplate/frame major-aura filter. */
  readonly category: AuraCategory;
}

/**
 * Per-def curation metadata. Mirror the aura defs in
 * `packages/content/data/auras/<id>.json`; the category reflects how prominent
 * the aura is on a crowded nameplate (a CC/defensive/key-debuff is major; an
 * incidental tick or passive is minor). Buff/debuff polarity is NOT here (#168).
 */
const AURA_META_BY_ID: Readonly<Record<string, AuraMeta>> = {
  // #163: keyed by opaque aura-def id (trailing comment = human slug).
  // #137: `category` curates the nameplate/frame major-aura rows.
  aur_Z20ELeJdWe: { category: "debuff" }, // slug: corruption — key magic debuff on the target
  aur_J8eoAOc8sD: { category: "cc" }, // slug: stun — hard CC
  aur_frhKCv4vu5: { category: "defensive" }, // slug: spellreflect — defensive self-buff
  aur_lk2JVljN35: { category: "minor" }, // slug: thorns — permanent passive reflect (not nameplate-major)
  // #932: curation backfill. These defs were live in content but absent here, so
  // they silently fell to DEFAULT_AURA_META (`minor`) and never reached a
  // nameplate. Categories follow the axis exactly as authored above — CC-class
  // defs mirror `stun`, key DoTs mirror `corruption`. No new category value.
  //
  // The three fear defs are one player-facing signal ("I am feared") from three
  // source abilities; the table is keyed by DEF ID, not CC class, so each needs
  // its own row. All three get the same category — no def-specific treatment.
  aur_3LeXMtaDAv: { category: "cc" }, // slug: polymorph-mez — hard CC (mez)
  aur_MNrZKXSekQ: { category: "cc" }, // slug: death-coil-fear — hard CC (fear)
  aur_KbdYCx1rcS: { category: "cc" }, // slug: fear — hard CC (fear)
  aur_8lwATrQJ42: { category: "cc" }, // slug: intimidating-shout-fear — hard CC (fear)
  aur_QvkGwrTxo1: { category: "debuff" }, // slug: unstable-affliction — key magic DoT
  aur_U0z2TqGSH2: { category: "debuff" }, // slug: rupture-dot — key physical DoT
  aur_tUpKHmA9Td: { category: "debuff" }, // slug: rend-dot — key physical DoT
};

/** Fallback for an unmapped def: categorised minor (an uncategorised aura is
 *  not curated-major, so it stays off nameplates by default until a row is
 *  added above).
 *
 *  KNOWN RECURRING GAP (#932): this default is SILENT — a newly authored aura is
 *  invisible on nameplates until someone remembers to add a row, with nothing
 *  failing to say so. #932 backfilled the specific defs requested; making the
 *  omission LOUD (a lint/test failure, or authoring `category` as a required
 *  content-schema field the way `polarity` became one in #168) is a separate,
 *  deliberately unscoped change. */
const DEFAULT_AURA_META: AuraMeta = { category: "minor" };


/** Canonical ability icon key per aura definition. Generic status auras intentionally
 * remain on the glyph fallback because they have no single source ability. */
function loadShieldAuraIds(): ReadonlySet<string> {
  const modules = import.meta.glob<Record<string, unknown>>("../../../../packages/content/data/auras/*.json", { eager: true, import: "default" });
  const ids = new Set<string>();
  for (const value of Object.values(modules)) {
    const primitives = value["primitives"];
    if (!Array.isArray(primitives)) continue;
    if (
      primitives.some(
        (primitive) =>
          typeof primitive === "object" &&
          primitive !== null &&
          (primitive as Record<string, unknown>)["kind"] === "absorbPool" &&
          (primitive as Record<string, unknown>)["mode"] === "shield",
      )
    ) {
      const id = value["id"];
      if (typeof id === "string") ids.add(id);
    }
  }
  return ids;
}

const SHIELD_AURA_IDS: ReadonlySet<string> = loadShieldAuraIds();

/** Whether an aura definition contains a shield-mode absorb pool. */
export function isShieldAura(defId: string): boolean {
  return SHIELD_AURA_IDS.has(defId);
}

const AURA_ICON_KEY_BY_ID: Readonly<Record<string, string>> = {
  'aur_Z20ELeJdWe': 'afflictor/corruption', // corruption
  'aur_waUiWOEy09': 'priest/renew', // renew
  'aur_ytHPFduf8k': 'fighter/battleshout', // battleshout-buff
  'aur_IuO8o6omyE': 'rogue/phase', // phase
  'aur_bpebmZkBow': 'hunter/bola-throw', // bola-root
  'aur_a26mnY73zF': 'mage/ice-barrier', // ice-barrier
  'aur_7r3ZkYzAeH': 'rogue/stealth', // stealth
  'aur_KbdYCx1rcS': 'afflictor/fear', // fear
  'aur_KNfjwa9d4m': 'fighter/defensive-strike', // defensive-strike-buff
  'aur_xfggKlR8hK': 'hunter/move-quick', // move-quick-haste
  'aur_frhKCv4vu5': 'fighter/spell-reflect-warrior', // spellreflect
  'aur_WmmFnubckw': 'priest/shadow-word-pain', // shadow-word-pain
  'aur_FXkSHtNttC': 'mage/frost-nova', // frost-nova-root
  'aur_siPWyYF5WG': 'mage/fireblast', // getting-hot
  'aur_vtETzBcZJ5': 'mage/frostbolt', // frostbolt-shatter-timer
  'aur_VzoTnsZTI5': 'priest/miracle', // miracle
  'aur_DzeJS7Tbhu': 'priest/power-word-shield', // power-word-shield
  'aur_15KhOpLGkG': 'mage/fireblast', // fireblast-instant-window
  'aur_MNrZKXSekQ': 'afflictor/death-coil', // death-coil-fear
  'aur_EWKCyJ0AWB': 'mage/fireball', // ignite
  'aur_3LeXMtaDAv': 'mage/polymorph', // polymorph-mez
  'aur_xYLU6B61J7': 'fighter/hamstring', // exhaustion
  'aur_29JJRQPvE4': 'rogue/shadowstep', // shadowstep-haste
  'aur_5L3EFe2IVM': 'mage/ice-block', // ice-block-buff
  'aur_r3TzsvTUPs': 'fighter/piercing-howl', // piercing-howl-slow
  'aur_d2nGoDYTP9': 'hunter/wing-clip', // wing-clip-slow
  'aur_e2g6i0BsLj': 'fighter/stay-mad', // stay-mad-buff
  'aur_q8i67eOsW9': 'hunter/poison-dart', // poison-dart-mez
  'aur_XzCV8l9NvA': 'rogue/fan-of-knives', // fok-slow
  'aur_60clF2cTDk': 'hunter/bandage', // bandage-hot
  'aur_Hl2nzUFq3S': 'afflictor/destruction', // destruction-dot
  'aur_KerXFD02my': 'fighter/hamstring', // hamstring-root
  'aur_ZwpcVnHhjH': 'hunter/harpoon', // harpoon-mark
  'aur_tGxHTOIsOq': 'rogue/dagger-throw', // dagger-throw-slow
  'aur_QvkGwrTxo1': 'afflictor/unstable-affliction', // unstable-affliction
  'aur_siJNV3uiOW': 'fighter/spell-reflect-warrior', // warrior-spell-reflect
  'aur_tUpKHmA9Td': 'fighter/rend', // rend-dot
  'aur_2lrk5OWgRR': 'mage/frost-geyser', // frost-geyser-root
  'aur_fIok0cI75Y': 'mage/cone-of-cold', // cone-of-cold-slow
  'aur_8lwATrQJ42': 'fighter/intimidating-shout', // intimidating-shout-fear
  'aur_RlgkOZ0xZW': 'priest/psychic-scream', // psychic-scream
  'aur_A4kXuQPUVq': 'fighter/stand-behind-me', // stand-behind-me
  'aur_Wv2uETP35P': 'mage/frostbolt', // frostbolt-slow
  'aur_U0z2TqGSH2': 'rogue/rupture', // rupture-dot
  'aur_OksBbuJqnS': 'hunter/serpent-arrow', // serpent-arrow-dot
  'aur_q3LEckuOjf': 'rogue/vanish', // vanish-immunity
};

/** Resolve an aura through the same canonical ability-art map as the ActionBar. */
export function auraIconSrc(defId: string): string | null {
  const key = AURA_ICON_KEY_BY_ID[defId];
  return key === undefined ? null : abilityIconSrcForKey(key);
}

/**
 * The single-character glyph fallback for an aura with no bundled icon: first
 * letter of the def id, uppercased. Mirrors the ActionBar letter-glyph fallback
 * (#72) so an unmapped aura always renders something legible.
 */
export function auraIconGlyph(defId: string): string {
  return defId.charAt(0).toUpperCase();
}

/** Curation category for an aura def (#137; unknown → minor). */
export function auraCategory(defId: string): AuraCategory {
  return (AURA_META_BY_ID[defId] ?? DEFAULT_AURA_META).category;
}
